/**
 * P8 메인 페이지 — "오늘 무엇이 중요한지" 비서 결의 first screen.
 * DESIGN.md anchors: Action card · List card · Hero Loop.
 */
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { listCallsFromRoom } from '../lib/roomData';
import { bridge } from '../lib/bridge';
import { ActionCard } from '../components/ActionCard';

function todayKoreanLabel(): string {
  const d = new Date();
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const days = ['일', '월', '화', '수', '목', '금', '토'];
  const dow = days[d.getDay()];
  return `${month}월 ${day}일 · ${dow}`;
}

function statusLabel(s: string): string {
  switch (s) {
    case 'captured': return '대기 중'; // privacy-first: 폰 캡처 직후 초기 상태
    case 'uploaded': return '대기 중'; // (구) 서버 업로드 모델 — 하위호환
    case 'transcribing': return '전사 중';
    case 'summarizing': return '요약 중';
    case 'ready': return '준비 완료';
    case 'failed': return '실패';
    default: return s;
  }
}

function durationLabel(sec: number | null): string {
  if (sec == null) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}분 ${s}초` : `${s}초`;
}

function timeShort(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

export function Home() {
  const navigate = useNavigate();

  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), retry: false });
  const suggestions = useQuery({
    queryKey: ['suggestions', 'pending'],
    queryFn: () => api.listSuggestions('pending'),
  });
  const todos = useQuery({
    queryKey: ['todos', 'open'],
    queryFn: () => api.listTodos(['new', 'in_progress']),
  });
  const calls = useQuery({
    queryKey: ['calls', 'recent'],
    queryFn: () => listCallsFromRoom(10),
  });

  if (me.isLoading) {
    return (
      <div className="flex items-center justify-center pt-3xl text-[var(--text-secondary)]">
        로딩 중…
      </div>
    );
  }
  // 실제 인증 실패(401)일 때만 로그아웃. 네트워크/서버 도달 실패는 로그아웃 사유가 아님 —
  // privacy-first: 로컬(폰 Room) 데이터는 서버 없이도 계속 보여준다.
  if (me.isError && me.error instanceof ApiError && me.error.status === 401) {
    bridge().clearAuthToken();
    navigate('/login', { replace: true });
    return null;
  }

  const pendingSuggestions = suggestions.data?.items ?? [];
  const openTodos = todos.data?.items ?? [];
  const recentCalls = calls.data?.items ?? [];

  return (
    <>
      <header className="px-xl pt-3xl pb-xl flex items-center justify-between">
        <div>
          <h1 className="font-display text-[28px] font-medium text-[var(--text-primary)] leading-tight">
            {me.data && 'name' in me.data && me.data.name ? `${me.data.name}님,` : '안녕하세요,'}
          </h1>
          <p className="text-[12px] text-[var(--text-muted)] tracking-wider uppercase mt-xs">
            {todayKoreanLabel()}
          </p>
        </div>
        <Link
          to="/more"
          aria-label="더보기"
          className="w-[40px] h-[40px] rounded-full bg-[var(--surface-elev)] border border-[var(--border)] grid place-items-center text-[14px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--accent)] transition-colors"
        >
          {(me.data && 'name' in me.data && me.data.name ? me.data.name : 'B').slice(0, 1)}
        </Link>
      </header>

      <main className="flex-1 px-xl space-y-lg">
        {/* 1) 오늘의 제안 */}
        {pendingSuggestions.length > 0 ? (
          <section>
            <h2 className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-sm">
              오늘의 제안
            </h2>
            <div className="space-y-sm">
              {pendingSuggestions.slice(0, 2).map((s) => (
                <ActionCard key={s.id} suggestion={s} />
              ))}
            </div>
          </section>
        ) : (
          <section className="bg-[var(--surface)] rounded-xl p-lg shadow-butler-md border border-[var(--border)] relative overflow-hidden">
            <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[var(--accent)]" />
            <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--accent)]">
              오늘
            </div>
            <h2 className="font-display text-[16px] font-medium text-[var(--text-primary)] mt-sm mb-sm leading-snug">
              아직 정리할 통화가 없어요.
            </h2>
            <p className="text-[13px] text-[var(--text-secondary)] leading-relaxed">
              통화가 끝나면 자동으로 요약을 만들어 이 자리에 올려드릴게요. 일정 등록 제안 카드도 같이 보입니다.
            </p>
          </section>
        )}

        {/* 2) 미해결 TODO */}
        <section>
          <div className="flex items-center justify-between mb-sm">
            <h2 className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">
              미해결 TODO {openTodos.length > 0 && <span className="text-[var(--text-secondary)] ml-xs">({openTodos.length})</span>}
            </h2>
            {openTodos.length > 3 && (
              <Link to="/todos" className="text-[12px] text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                모두 보기 →
              </Link>
            )}
          </div>
          {openTodos.length === 0 ? (
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-butler-sm px-lg py-xl text-center">
              <p className="text-[13px] text-[var(--text-muted)] leading-relaxed">
                통화·SMS에서 해야 할 일을 자동으로 찾아드릴게요.
              </p>
            </div>
          ) : (
            <ul className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-butler-md overflow-hidden divide-y divide-[var(--border)]">
              {openTodos.slice(0, 3).map((t) => (
                <li key={t.id} className="px-lg py-md flex items-start gap-md">
                  <span className="text-[var(--accent)] text-[14px] mt-[2px]">✦</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] text-[var(--text-primary)] truncate">
                      {t.content}
                    </div>
                    <div className="text-[11px] text-[var(--text-muted)] tracking-wider uppercase mt-[2px]">
                      {t.status === 'in_progress' ? '진행 중' : '새로운'}
                      {t.dueAt && ` · 마감 ${new Date(t.dueAt).toLocaleDateString('ko-KR')}`}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* 3) 최근 통화 */}
        <section>
          <div className="flex items-center justify-between mb-sm">
            <h2 className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">
              최근 통화
            </h2>
            {recentCalls.length > 3 && (
              <Link to="/calls" className="text-[12px] text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                모두 보기 →
              </Link>
            )}
          </div>
          {recentCalls.length === 0 ? (
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-butler-sm px-lg py-xl text-center">
              <p className="text-[13px] text-[var(--text-muted)] leading-relaxed">
                통화가 쌓이면 여기에 자동으로 정리됩니다.
              </p>
            </div>
          ) : (
            <ul className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-butler-md overflow-hidden divide-y divide-[var(--border)]">
              {recentCalls.slice(0, 3).map((c) => (
                <li key={c.id}>
                  <Link
                    to={`/calls/${c.id}`}
                    className="flex items-center gap-md px-lg py-md hover:bg-[var(--surface-elev)] transition-colors"
                  >
                    <div className="w-[32px] h-[32px] rounded-md bg-[var(--surface-elev)] grid place-items-center text-[14px] text-[var(--text-secondary)]">
                      📞
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-medium text-[var(--text-primary)] truncate">
                        {c.contact_name ?? c.contact_phone ?? '알 수 없음'}
                      </div>
                      <div className="text-[12px] text-[var(--text-muted)] mt-[2px] truncate">
                        {statusLabel(c.status)} · {durationLabel(c.duration_seconds)}
                      </div>
                    </div>
                    <div className="text-[11px] text-[var(--text-muted)] font-mono shrink-0">
                      {timeShort(c.started_at)}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* 4) 채팅 진입 */}
        <section>
          <Link
            to="/chat"
            className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-butler-md p-md flex items-center gap-md hover:bg-[var(--surface-elev)] transition-colors"
          >
            <div className="w-[32px] h-[32px] rounded-md bg-[var(--accent)] text-[var(--accent-on)] grid place-items-center font-display font-bold text-[14px]">
              B
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-[var(--text-primary)]">
                Butler에게 물어보세요…
              </div>
              <div className="text-[11px] text-[var(--text-muted)] mt-[2px]">
                지난 60일 통화·30일 SMS 컨텍스트로 답변
              </div>
            </div>
            <div className="text-[var(--text-muted)]">→</div>
          </Link>
        </section>
      </main>
    </>
  );
}
